import React, { useState, useEffect } from 'react';
import { Send, Copy, X, Heart, Smile, Award, Star } from 'lucide-react';
import { toast } from 'react-hot-toast';
import recommendationLetterService from '../../services/recommendationLetterService';
import { useFamilyContext } from '../../contexts/FamilyContext';

const ThankYouEmailComposer = ({ 
  recommendation, 
  isOpen, 
  onClose
}) => {
  const { currentUser } = useFamilyContext();
  const [selectedTemplate, setSelectedTemplate] = useState('heartfelt');
  const [customMessage, setCustomMessage] = useState('');
  const [collegeDecision, setCollegeDecision] = useState('');
  const [specificMemory, setSpecificMemory] = useState('');

  const templates = {
    heartfelt: {
      name: 'Heartfelt & Personal',
      icon: <Heart className="w-4 h-4" />,
      generate: () => {
        const studentName = currentUser?.name || 'Student';
        return `Dear ${recommendation?.recommender_name || '[Teacher Name]'},

I wanted to reach out to express my deepest gratitude for writing my letter of recommendation. Your support throughout this college application process has meant the world to me.

${specificMemory ? `I'll always remember ${specificMemory}. This experience` : 'Your class'} not only helped me grow academically but also gave me the confidence to pursue my goals. Your belief in me made such a difference during this challenging time.

${collegeDecision ? `I'm excited to share that ${collegeDecision}! ` : ''}I know that your recommendation played a crucial role in my applications, and I'm so grateful for the time and effort you put into supporting me.

Thank you for being such an incredible teacher and mentor. Your impact on my life extends far beyond the classroom, and I'll carry the lessons you've taught me throughout my college journey and beyond.

With sincere appreciation,
${studentName}`;
      }
    },
    professional: {
      name: 'Professional & Concise',
      icon: <Award className="w-4 h-4" />,
      generate: () => {
        const studentName = currentUser?.name || 'Student';
        return `Dear ${recommendation?.recommender_name || '[Teacher Name]'},

I wanted to formally thank you for writing my letter of recommendation for my college applications. Your support has been invaluable throughout this process.

${collegeDecision ? `I'm pleased to inform you that ${collegeDecision}. ` : ''}Your recommendation undoubtedly strengthened my applications, and I deeply appreciate the time you took to advocate on my behalf.

${specificMemory ? `Your ${specificMemory} has had a lasting impact on my academic journey. ` : ''}The knowledge and skills I gained in your class have prepared me well for the challenges ahead.

Thank you again for your guidance and support. I look forward to staying in touch and updating you on my college experience.

Best regards,
${studentName}`;
      }
    },
    enthusiastic: {
      name: 'Enthusiastic & Upbeat',
      icon: <Smile className="w-4 h-4" />,
      generate: () => {
        const studentName = currentUser?.name || 'Student';
        return `Dear ${recommendation?.recommender_name || '[Teacher Name]'},

I hope this email finds you well! I couldn't wait any longer to thank you for writing my recommendation letter - you're absolutely amazing!

${collegeDecision ? `Guess what? ${collegeDecision}! I'm still in shock! ` : 'The application process is moving along, and '}Your recommendation has been such a huge part of my journey. When I think about all the support you've given me, I feel so lucky to have had you as my teacher.

${specificMemory ? `I smile every time I think about ${specificMemory}. That moment really shaped who I am today! ` : ''}Your class wasn't just about learning ${recommendation?.subject || 'the subject'} - it was about discovering what I'm capable of.

I promise to make you proud in college! Thank you for believing in me and for being such an incredible mentor. I'll definitely keep you updated on my adventures!

With excitement and gratitude,
${studentName}`;
      }
    },
    update: {
      name: 'Update & Thank You',
      icon: <Star className="w-4 h-4" />,
      generate: () => {
        const studentName = currentUser?.name || 'Student';
        return `Dear ${recommendation?.recommender_name || '[Teacher Name]'},

I wanted to give you an update on my college applications and thank you once again for your letter of recommendation.

${collegeDecision ? `I'm thrilled to share that ${collegeDecision}! This wouldn't have been possible without your support. ` : "I'm still waiting to hear back from schools, but I wanted to thank you regardless of the outcome. "}Your recommendation has given me confidence throughout this entire process.

${specificMemory ? `Looking back, ${specificMemory} was a turning point for me. ` : ''}Your class taught me to think critically and pursue my passions with determination. These lessons have been invaluable as I've navigated the college application process.

I wanted you to know how much your mentorship has meant to me. ${collegeDecision ? "I'll be sure to visit and share more about my college plans when I get the chance." : "I'll keep you posted as decisions come in."}

Thank you for everything you've done for me. Your impact on my life has been profound.

Warmly,
${studentName}`;
      }
    }
  };

  const handleCopyEmail = async (template = null) => {
    try {
      const emailBody = template ? templates[template].generate() : customMessage;
      const subject = `Thank You - ${currentUser?.name || 'Student'}`;
      const fullEmail = `Subject: ${subject}\n\n${emailBody}`;
      
      await navigator.clipboard.writeText(fullEmail);
      toast.success('Thank you email copied to clipboard!');
      
      // Save to history
      if (recommendation?.id) {
        await recommendationLetterService.saveEmailContent(recommendation.id, {
          type: 'thank_you',
          content: emailBody,
          status: 'draft'
        });
      }
    } catch (error) {
      console.error('Error copying email:', error);
      toast.error('Failed to copy email');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-full sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-red-500" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Thank You Email</h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  Express your gratitude to {recommendation?.recommender_name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Context Fields */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                College Decision (Optional)
              </label>
              <input
                type="text"
                value={collegeDecision}
                onChange={(e) => setCollegeDecision(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="e.g., I've been accepted to MIT! or I'm going to Stanford!"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Specific Memory or Impact (Optional)
              </label>
              <input
                type="text"
                value={specificMemory}
                onChange={(e) => setSpecificMemory(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="e.g., when you stayed after class to help me understand calculus"
              />
            </div>
          </div>

          {/* Template Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Choose a Template</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(templates).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTemplate(key)}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedTemplate === key
                      ? 'border-red-500 bg-red-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {template.icon}
                    <span className="font-medium text-slate-900">{template.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3">Preview</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                {templates[selectedTemplate].generate()}
              </pre>
            </div>
          </div>

          {/* Custom Message Option */}
          <div className="mt-6">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                Write Custom Message Instead
              </summary>
              <div className="mt-3">
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="w-full h-48 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  placeholder="Write your own thank you message..."
                />
                <button
                  onClick={() => handleCopyEmail(null)}
                  disabled={!customMessage.trim()}
                  className="mt-3 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Copy className="w-4 h-4" />
                  Copy Custom Message
                </button>
              </div>
            </details>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Remember to personalize before sending!
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleCopyEmail(selectedTemplate)}
                className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy Selected Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouEmailComposer;